/**
 * 認証API層
 * ガイドラインに従ったRESTfulなAPI設計
 */

import axios from 'axios';
import type { 
  LoginFormData, 
  NFCLoginData, 
  AuthResponse, 
  User,
  Staff,
  AuthError,
  TokenMetadata
} from '@/features/auth/types';
import type {
  LoginApiResponse,
  NFCLoginApiResponse,
  CurrentUserApiResponse,
  PasswordResetApiResponse,
  PasswordResetRequest,
  PasswordResetConfirmRequest
} from '@/features/auth/types/api';

// APIクライアント設定
const apiClient = axios.create({
  baseURL: '/api/v3', // v3 APIを使用（統一）
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// トークンをヘッダーに設定
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 認証APIエンドポイント定義
export const authApi = {
  /**
   * メールとパスワードでログイン
   */
  login: async (data: LoginFormData): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>('/auth/login', {
      ...data,
      device_name: 'web'
    });
    
    // Type-safe response handling
    if (!response.data.success) {
      throw new AuthApiError(
        response.data.error?.message || 'Login failed',
        response.status
      );
    }
    
    const responseData = response.data.data;
    if (!responseData?.token) {
      throw new AuthApiError('No token received from server', response.status);
    }
    
    return {
      success: true,
      data: {
        token: responseData.token,
        staff: responseData.user as Staff,
        user: responseData.user,
        expires_in: responseData.expires_in,
        token_type: responseData.token_type || 'Bearer',
      },
      message: response.data.message || undefined,
      timestamp: response.data.timestamp || undefined,
    };
  },

  /**
   * NFCカードでログイン
   */
  nfcLogin: async (data: NFCLoginData): Promise<AuthResponse> => {
    const response = await apiClient.post<NFCLoginApiResponse>('/auth/nfc-login', data);
    
    // Type-safe response handling
    if (!response.data.success) {
      throw new AuthApiError(
        response.data.error?.message || 'NFC login failed',
        response.status
      );
    }
    
    const responseData = response.data.data;
    if (!responseData?.token) {
      throw new AuthApiError('No token received from server', response.status);
    }
    
    return {
      success: true,
      data: {
        token: responseData.token,
        staff: responseData.staff,
        user: responseData.staff as User,
        expires_in: responseData.expires_in,
        token_type: responseData.token_type || 'Bearer',
      },
      message: response.data.message || undefined,
      timestamp: response.data.timestamp || undefined,
    };
  },

  /**
   * ログアウト
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  /**
   * 現在のユーザー情報取得
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<CurrentUserApiResponse>('/auth/me');
    
    if (!response.data.success) {
      throw new AuthApiError(
        response.data.error?.message || 'Failed to get current user',
        response.status
      );
    }
    
    const userData = response.data.data;
    if (!userData) {
      throw new AuthApiError('No user data received', response.status);
    }
    
    return userData;
  },

  /**
   * トークンの検証
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await apiClient.get('/auth/verify');
      return true;
    } catch {
      return false;
    }
  },

  /**
   * パスワードリセットリクエスト
   */
  requestPasswordReset: async (email: string): Promise<void> => {
    const request: PasswordResetRequest = { email };
    const response = await apiClient.post<PasswordResetApiResponse>('/auth/forgot-password', request);
    
    if (!response.data.success) {
      throw new AuthApiError(
        response.data.error?.message || 'Password reset request failed',
        response.status
      );
    }
  },

  /**
   * パスワードリセット実行
   */
  resetPassword: async (data: PasswordResetConfirmRequest): Promise<void> => {
    const response = await apiClient.post<PasswordResetApiResponse>('/auth/reset-password', data);
    
    if (!response.data.success) {
      throw new AuthApiError(
        response.data.error?.message || 'Password reset failed',
        response.status
      );
    }
  },
};

// トークン管理ユーティリティ（コンテキスト問題修正版）
const createTokenManager = () => {
  const getToken = (): string | null => {
    const token = localStorage.getItem('auth_token');
    const metadata = localStorage.getItem('auth_token_metadata');
    
    if (!token) return null;
    
    // メタデータがある場合は有効期限をチェック
    if (metadata) {
      try {
        const tokenData: TokenMetadata = JSON.parse(metadata);
        if (Date.now() > tokenData.expiresAt) {
          // 期限切れの場合はトークンを削除
          removeToken();
          return null;
        }
      } catch (error) {
        // メタデータのパースに失敗した場合は続行
        console.warn('Failed to parse token metadata:', error);
      }
    }
    
    return token;
  };

  const removeToken = (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token_metadata');
    
    // Axiosデフォルトヘッダーからも削除
    delete axios.defaults.headers.common['Authorization'];
  };

  const getTokenExpiration = (): Date | null => {
    const metadata = localStorage.getItem('auth_token_metadata');
    if (!metadata) return null;
    
    try {
      const tokenData: TokenMetadata = JSON.parse(metadata);
      return new Date(tokenData.expiresAt);
    } catch (error) {
      console.warn('Failed to parse token metadata for expiration:', error);
      return null;
    }
  };

  return {
    /**
     * トークンを安全に保存（有効期限付き）
     */
    setToken: (token: string, expiresInMinutes: number = 480): void => { // デフォルト 8時間
      const expirationTime = Date.now() + (expiresInMinutes * 60 * 1000);
      const tokenData = {
        token,
        expiresAt: expirationTime,
        createdAt: Date.now(),
      };
      
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_token_metadata', JSON.stringify(tokenData));
      
      // Axiosデフォルトヘッダーも更新
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    },

    /**
     * トークンを取得（有効期限チェック付き）
     */
    getToken,

    /**
     * トークンを安全に削除
     */
    removeToken,

    /**
     * 有効なトークンが存在するかチェック
     */
    hasToken: (): boolean => {
      return !!getToken();
    },
    
    /**
     * トークンの有効期限を取得
     */
    getTokenExpiration,
    
    /**
     * トークンが間もなく期限切れになるかチェック
     */
    isTokenExpiringSoon: (thresholdMinutes: number = 30): boolean => {
      const expiration = getTokenExpiration();
      if (!expiration) return false;
      
      const thresholdTime = Date.now() + (thresholdMinutes * 60 * 1000);
      return expiration.getTime() < thresholdTime;
    },
  };
};

// シングルトンインスタンスをエクスポート
export const tokenManager = createTokenManager();

// カスタムエラークラス（強化された型安全性）
export class AuthApiError extends Error implements AuthError {
  public readonly name = 'AuthApiError';
  public readonly timestamp: string;
  
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly errors?: Record<string, string[]>,
    public readonly response?: {
      data?: Record<string, any>;
      status?: number;
      statusText?: string;
    },
    public readonly retryAfter?: number
  ) {
    super(message);
    this.timestamp = new Date().toISOString();
    
    // Ensure proper prototype chain
    Object.setPrototypeOf(this, AuthApiError.prototype);
  }
  
  /**
   * Create error from response
   */
  static fromResponse(response: any): AuthApiError {
    const status = response?.status;
    const data = response?.data;
    
    return new AuthApiError(
      data?.error?.message || data?.message || 'API Error',
      status,
      data?.error?.code,
      data?.errors,
      {
        data,
        status: response?.status,
        statusText: response?.statusText,
      }
    );
  }
  
  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return this.status ? this.status >= 500 || this.status === 429 : false;
  }
  
  /**
   * Check if error is due to authentication
   */
  isAuthenticationError(): boolean {
    return this.status === 401;
  }
  
  /**
   * Check if error is due to authorization
   */
  isAuthorizationError(): boolean {
    return this.status === 403;
  }
  
  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.status) {
      case 401:
        return 'メールアドレスまたはパスワードが正しくありません';
      case 403:
        return 'このアクションを実行する権限がありません';
      case 429:
        return 'アクセス制限中です。しばらく待ってから再試行してください';
      case 422:
        return '入力内容に問題があります';
      case 500:
      case 502:
      case 503:
        return 'サーバーエラーが発生しました。しばらく待ってから再試行してください';
      default:
        return this.message;
    }
  }
}